import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Controller, useForm } from 'react-hook-form'
import { InferType } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQuery } from '@tanstack/react-query'
import { captureException } from '@sentry/react'
import { Button, ButtonVariant } from '../../../common/Button'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { TextInput } from '../../../common/TextInput'
import { useFormErrorMessage } from '../../../../hooks/useFormErrorMessage'
import { FormControl } from '../../../common/FormControl'
import { SelectDropdown, SelectOption } from '../../../common/SelectDropdown'
import { societyPreApprovedCreateSchema } from '../../../../forms/schemas/society_pre_approved_create'
import { ResidentUserType } from '../../../../state/stores/units'
import { UnitRole } from '../../../../types/unit-roles'
import { getUnitRole } from '../../../../helpers/translations/unit-roles'
import { FormSpacing } from '../../../common/FormSpacing'
import { ISocietiesModel } from '../../../../interfaces/models/societies.interfaces'
import { entranceQueries } from '../../../../api/society-entrances'
import { unitQueries } from '../../../../api/units'
import { Toggle } from '../../../common/Toggle'
import { translateRole } from '../../../../helpers/society'
import { BoardRole } from '../../../../types/board-roles'
import { Label } from '../../../common/Label'
import { theme } from '../../../../theme/theme'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { ToastType } from '../../../common/Toast/toast-type'
import { i18nSocietyType } from '../../../../i18n'

interface InviteFormProps {
  onError: () => void
  onSubmit: (
    data: InferType<typeof societyPreApprovedCreateSchema>
  ) => Promise<void>
  onClose: () => void
  loading: boolean
  society: ISocietiesModel
  residentType: ResidentUserType
}

export const InviteForm = ({
  loading,
  onError,
  onSubmit,
  onClose,
  society,
  residentType,
}: InviteFormProps): ReactNode => {
  const { translate } = useAppTranslation()
  const { getErrorMessage } = useFormErrorMessage()
  const [inviteAsBoard, setInviteAsBoard] = useState<boolean>(false)
  const [inviteWithoutUnitError, setInviteWithoutUnitError] =
    useState<string>('')

  const [isUnassigned, setIsUnassgined] = useState<boolean>(false)
  const { setToastNotification } = useToastNotifications()

  const { data: entrances } = useQuery(
    entranceQueries.getEntrancesPerSociety(society._id)
  )

  const getDefaultValues = useCallback((): InferType<
    typeof societyPreApprovedCreateSchema
  > => {
    return {
      societyId: society._id,
      userEmail: '',
      entranceId: entrances && entrances.length > 0 ? entrances[0]._id : '',
      unitIdOrTitle: '',
      unitRole:
        residentType === ResidentUserType.NON_MEMBER
          ? UnitRole.RESIDENT
          : UnitRole.MEMBER,
      inviteAsAdmin: false,
      boardRole: undefined,
      isUnassigned: false,
    }
  }, [society, entrances, residentType])

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    setValue,
    resetField,
  } = useForm({
    mode: 'all',
    resolver: yupResolver(societyPreApprovedCreateSchema),
    defaultValues: getDefaultValues(),
  })

  const { data: units } = useQuery(unitQueries.getUnitsPerSociety(society._id))

  const watchEntranceId = watch('entranceId')

  const entranceOptions =
    entrances?.map((_entrance) => ({
      label: _entrance.addressStreet as string,
      value: _entrance._id,
    })) || []

  const entranceUnitOptions: SelectOption[] =
    units
      ?.filter((_unit) => _unit.entranceId === watchEntranceId)
      .map((_unit) => ({
        value: _unit._id,
        label: `${_unit.titleLegal} (${_unit.title})`,
      })) || []

  const allowedUnitRules = (): { [key: string]: UnitRole } => {
    if (!residentType) {
      return { ...UnitRole }
    }

    const { MEMBER, ...rest } = UnitRole

    if (residentType === ResidentUserType.MEMBER) {
      return { MEMBER }
    }

    return { ...rest }
  }

  const boardRoleOptions = useMemo(
    () =>
      Object.values(BoardRole).map((val) => ({
        value: val as string,
        label: translateRole(val) as string | '',
      })),
    []
  )

  const unitRoleOptions = Object.values(allowedUnitRules()).map((val) => ({
    value: val as string,
    label: translate(getUnitRole(val) as string),
  }))

  const unAssignedUnit = units?.find((unit) => unit.isUnassigned === true)

  const hasNoUnits =
    units?.filter((unit) => unit.isUnassigned !== true).length === 0

  useEffect(() => {
    if (hasNoUnits && unAssignedUnit) {
      setIsUnassgined(true)
      setValue('entranceId', unAssignedUnit.entranceId || '')
      setValue('unitIdOrTitle', unAssignedUnit._id)
      setValue('isUnassigned', true)
    }
  }, [hasNoUnits, unAssignedUnit, setValue])

  return (
    <FormSpacing>
      {!hasNoUnits && (
        <FormControl name="inviteWithoutUnit" error={inviteWithoutUnitError}>
          <div className="flex justify-between flex-row">
            <Label
              name="inviteWithoutUnit"
              label={translate('registerInvite.form.inviteWithoutUnit', {
                context: i18nSocietyType(society?.societyTypeDisplayName),
              })}
              style={theme.textVariants.baseBold}
            />
            <Toggle
              name="inviteWithoutUnit"
              onChange={(value) => {
                if (!unAssignedUnit) {
                  setInviteWithoutUnitError(
                    translate('registerInvite.form.unitMissingMessage', {
                      context: i18nSocietyType(society?.societyTypeDisplayName),
                    }) + society.name
                  )
                  setToastNotification(
                    ToastType.DANGER,
                    translate('registerInvite.form.unitMissingMessage', {
                      context: i18nSocietyType(society?.societyTypeDisplayName),
                    }) + society.name
                  )
                  captureException(
                    translate('registerInvite.form.unitMissingMessage', {
                      context: i18nSocietyType(society?.societyTypeDisplayName),
                    }),
                    { data: society }
                  )
                } else {
                  setIsUnassgined(value)
                  if (value) {
                    setValue('entranceId', unAssignedUnit?.entranceId || '')
                    setValue('unitIdOrTitle', unAssignedUnit?._id || '')
                  } else {
                    setValue('unitIdOrTitle', '', { shouldValidate: true })
                  }
                }
              }}
              enabled={isUnassigned}
            />
          </div>
        </FormControl>
      )}
      {!isUnassigned && !hasNoUnits && (
        <>
          <Controller
            control={control}
            name="entranceId"
            render={({ field: { value, name, onChange } }) => (
              <FormControl
                label={translate('registerView.unit.labels.entrance', {
                  context: i18nSocietyType(society?.societyTypeDisplayName),
                })}
                error={errors.entranceId && getErrorMessage(errors.entranceId)}
                name={name}
              >
                <SelectDropdown
                  options={entranceOptions}
                  value={entranceOptions.find(
                    (option) => option.value === value
                  )}
                  onChange={(option) => {
                    onChange(option?.value)
                    setValue('unitIdOrTitle', '', {
                      shouldValidate: true,
                    })
                  }}
                  error={!!errors.entranceId}
                />
              </FormControl>
            )}
          />
          <Controller
            control={control}
            name="unitIdOrTitle"
            render={({ field: { value, name, onChange } }) => (
              <FormControl
                label={translate('registerInvite.form.unit', {
                  context: i18nSocietyType(society?.societyTypeDisplayName),
                })}
                error={
                  errors.unitIdOrTitle && getErrorMessage(errors.unitIdOrTitle)
                }
                name={name}
              >
                <SelectDropdown
                  options={entranceUnitOptions}
                  value={
                    entranceUnitOptions.find(
                      (option) => option.value === value
                    ) || ''
                  }
                  onChange={(option) =>
                    typeof option === 'object' && onChange(option?.value)
                  }
                  error={!!errors.unitIdOrTitle}
                />
              </FormControl>
            )}
          />
        </>
      )}
      {residentType !== ResidentUserType.MEMBER && (
        <Controller
          control={control}
          name="unitRole"
          render={({ field: { value, name, onChange } }) => (
            <FormControl
              label={translate('common.form.labels.role')}
              error={errors.unitRole && getErrorMessage(errors.unitRole)}
              name={name}
            >
              <SelectDropdown
                value={unitRoleOptions.find((option) => option.value === value)}
                onChange={(option) => onChange(option?.value)}
                options={unitRoleOptions}
                error={!!errors.unitRole}
              />
            </FormControl>
          )}
        />
      )}
      <Controller
        control={control}
        name="userEmail"
        render={({ field: { value, name, onChange, onBlur } }) => (
          <FormControl
            label={translate('common.form.labels.email')}
            error={errors.userEmail && getErrorMessage(errors.userEmail)}
            name={name}
          >
            <TextInput
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              error={!!errors.userEmail}
              type="email"
            />
          </FormControl>
        )}
      />
      <Controller
        control={control}
        name="inviteAsAdmin"
        render={({ field: { value, onChange, name } }) => (
          <div className="flex justify-between">
            <Label
              name={name}
              label={translate('registerInvite.form.inviteAsAdmin')}
              style={theme.textVariants.baseBold}
            />
            <Toggle name={name} enabled={!!value} onChange={onChange} />
          </div>
        )}
      />

      <div className="flex justify-between">
        <Label
          name="inviteAsBoardToggle"
          label={translate('registerInvite.form.inviteAsBoard', {
            context: i18nSocietyType(society?.societyTypeDisplayName),
          })}
          style={theme.textVariants.baseBold}
        />
        <Toggle
          name="inviteAsBoardToggle"
          onChange={(value) => {
            setInviteAsBoard(value)
            value && setValue('boardRole', boardRoleOptions?.[0]?.value)
            !value && resetField('boardRole', undefined)
          }}
          enabled={inviteAsBoard}
        />
      </div>
      {inviteAsBoard && (
        <Controller
          control={control}
          name="boardRole"
          render={({ field: { onChange, value, name } }) => {
            return (
              <FormControl
                label={translate('createUpdateBoardMemberForm.labels.role')}
                name={name}
              >
                <SelectDropdown
                  options={boardRoleOptions}
                  value={boardRoleOptions.find(
                    (option) => option.value === value
                  )}
                  onChange={(option) =>
                    typeof option === 'object' && onChange(option?.value)
                  }
                  isDisabled={!inviteAsBoard}
                />
              </FormControl>
            )
          }}
        />
      )}

      <div className="mt-2 flex items-center justify-end gap-4">
        <Button label={translate('common.actions.cancel')} onClick={onClose} />
        <Button
          disabled={!isValid || loading}
          variant={ButtonVariant.PRIMARY}
          label={translate('common.actions.invite')}
          type="submit"
          onClick={handleSubmit(onSubmit, onError)}
        />
      </div>
    </FormSpacing>
  )
}
